'use client';

import { useState } from 'react';
import { Play, ArrowRight, Search } from 'lucide-react';
import { SvgIcon } from '@/components/shared/SvgIcon';
import { DemoCard } from '@/components/demos/DemoCard';
import { DemoModal } from '@/components/demos/DemoModal';
import { useDemoModal } from '@/components/demos/DemoModalProvider';
import { getDotcmsImageURL } from '@/app/utils/getDotcmsImageURL';

const CATEGORIES = [
    'All Demos',
    'Content Management',
    'Intranet Experience',
    'Governance',
    'Multisite Scale',
    'Visual Editing'
];

export function DemoCenter({ demos = [] }) {
    const featured = demos.find((d) => d.isFeatured);

    return (
        <div className="min-h-screen bg-white text-blue-700">
            {featured && <Hero featured={featured} />}
            <DemoGrid demos={demos} />
            <CTA />
        </div>
    );
}

function Hero({ featured }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { openScheduleModal } = useDemoModal();
    const thumbSrc =
        featured.thumbnail?.idPath ?? featured.thumbnail?.identifier ?? featured.thumbnail;

    return (
        <section className="relative overflow-hidden text-white" style={{ background: '#0B0B26' }}>
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-28 pt-24 lg:grid-cols-12 lg:gap-10 lg:pt-32">
                <div className="lg:col-span-7">
                    <span className="inline-flex items-center rounded-full border border-white/20 bg-white/5 px-3.5 py-1 text-xs font-semibold uppercase tracking-widest text-white/90 backdrop-blur">
                        Curious how dotCMS works?
                    </span>
                    <h1 className="sm:text-6xl mt-7 text-5xl font-semibold leading-[1.15] tracking-tight">
                        Start with a guided tour and see how content moves{' '}
                        <span className="text-fuschia-700">from creation to delivery.</span>
                    </h1>
                    <p className="text-lg mt-7 max-w-xl leading-relaxed text-white/70">
                        Explore content modeling, visual editing, workflows, APIs, and governance
                        through interactive product tours — all at your own pace.
                    </p>
                    <div className="mt-9 flex flex-wrap items-center gap-3">
                        <a
                            href="#demos"
                            className="inline-flex items-center gap-2 rounded-md bg-fuschia-700 px-6 py-3.5 text-base font-semibold text-white shadow-lg transition hover:scale-[1.02]">
                            Start exploring
                            <ArrowRight className="size-4" />
                        </a>
                        <button
                            type="button"
                            onClick={() => openScheduleModal('demo_center_hero')}
                            className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-6 py-3.5 text-base font-semibold text-white backdrop-blur transition hover:bg-white/10">
                            Talk to Sales
                        </button>
                    </div>
                </div>

                <div className="lg:col-span-5">
                    <div
                        className="relative rounded-3xl p-6"
                        style={{
                            background: 'rgba(255,255,255,0.03)',
                            boxShadow:
                                'inset 0 0 0 1px rgba(255,255,255,0.08), 0 30px 80px -20px rgba(124,58,237,0.35)'
                        }}>
                        <div className="flex items-start justify-between">
                            <div className="flex size-11 items-center justify-center rounded-xl bg-fuschia-700 shadow-lg">
                                <SvgIcon
                                    icon={featured.demoIcon}
                                    size={20}
                                    className="text-white"
                                />
                            </div>
                            <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-white/80">
                                Featured
                            </span>
                        </div>
                        <h3 className="mt-5 text-2xl font-semibold text-white">{featured.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-white/70">
                            {featured.description}
                        </p>
                        {featured.framecode ? (
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="mt-5 inline-flex items-center gap-2 rounded-md bg-white px-5 py-2.5 text-base font-semibold text-blue-700 transition hover:bg-white/90">
                                <Play className="size-4 fill-current" />
                                Launch Tour
                            </button>
                        ) : (
                            <a
                                href={`/demo-center/${featured.urlTitle}`}
                                className="mt-5 inline-flex items-center gap-2 rounded-md bg-white px-5 py-2.5 text-base font-semibold text-blue-700 transition hover:bg-white/90">
                                <Play className="size-4 fill-current" />
                                Launch Tour
                            </a>
                        )}

                        {/* Product preview mock */}
                        <div className="mt-6 overflow-hidden rounded-2xl bg-gray-100 p-4 shadow-2xl">
                            <div className="rounded-xl bg-white p-3 shadow-lg">
                                <div className="flex items-center gap-1.5 border-b border-black/5 pb-2">
                                    <span className="size-2 rounded-full bg-red-400" />
                                    <span className="size-2 rounded-full bg-yellow-400" />
                                    <span className="size-2 rounded-full bg-green-400" />
                                    <span className="ml-2 truncate text-xs font-medium text-blue-500">
                                        demo.dotcms.com / pages
                                    </span>
                                </div>
                                <div className="grid grid-cols-4 gap-2 pt-3">
                                    <div className="col-span-1 space-y-1.5">
                                        <div className="h-2 rounded bg-fuschia-700/30" />
                                        <div className="h-2 rounded bg-blue-700/10" />
                                        <div className="h-2 rounded bg-blue-700/10" />
                                        <div className="h-2 rounded bg-blue-700/10" />
                                    </div>
                                    <div className="col-span-3 space-y-2">
                                        <div className="h-16 rounded-lg bg-fuschia-700/20" />
                                        <div className="grid grid-cols-3 gap-1.5">
                                            <div className="h-10 rounded-md bg-blue-700/10" />
                                            <div className="h-10 rounded-md bg-blue-700/10" />
                                            <div className="h-10 rounded-md bg-blue-700/10" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <DemoModal
                    frameCode={featured.framecode}
                    previewImage={
                        thumbSrc ? getDotcmsImageURL({ src: thumbSrc, width: 1600 }) : undefined
                    }
                    onClose={() => setIsModalOpen(false)}
                    onScheduleDemo={() => openScheduleModal('tour_modal')}
                />
            )}
        </section>
    );
}

function DemoGrid({ demos }) {
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState('All Demos');

    const filtered = demos.filter((d) => {
        const matchesCategory = activeCategory === 'All Demos' || d.category === activeCategory;
        const matchesSearch =
            !search ||
            d.title.toLowerCase().includes(search.toLowerCase()) ||
            d.description.toLowerCase().includes(search.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    return (
        <section id="demos" className="mx-auto max-w-7xl px-6 py-24">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl">
                        Explore All dotCMS Demos
                    </h2>
                    <p className="mt-3 max-w-xl text-base text-blue-500">
                        Choose a tour by capability
                    </p>
                </div>
                <div className="relative w-full sm:w-72">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-blue-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search demos…"
                        className="h-11 w-full rounded-full border border-blue-200 bg-blue-100/40 pl-10 pr-4 text-sm outline-none transition focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10"
                    />
                </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                    <button
                        key={c}
                        onClick={() => setActiveCategory(c)}
                        className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                            activeCategory === c
                                ? 'border-primary bg-fuschia-700 text-white'
                                : 'border-blue-200 bg-white text-blue-600 hover:border-blue-400 hover:text-blue-700'
                        }`}>
                        {c}
                    </button>
                ))}
            </div>

            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map((d) => (
                    <DemoCard key={d.identifier} demo={d} />
                ))}
            </div>

            {filtered.length === 0 && (
                <p className="mt-16 text-center text-blue-500">
                    No demos match your search. Try a different term.
                </p>
            )}
        </section>
    );
}

function CTA() {
    return (
        <section className="mx-auto hidden max-w-7xl px-6 pb-24">
            <div className="relative overflow-hidden rounded-[2rem] bg-blue-700 px-8 py-16 text-white sm:px-16 sm:py-20">
                <div className="relative grid gap-8 lg:grid-cols-5 lg:items-center">
                    <div className="lg:col-span-3">
                        <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                            Ready to see dotCMS on your own content?
                        </h2>
                        <p className="mt-3 max-w-xl text-white/70">
                            Get a guided walkthrough from our team — tailored to your stack, your
                            channels, and your governance needs.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3 lg:col-span-2 lg:justify-end">
                        <a
                            href="#"
                            className="inline-flex items-center gap-2 rounded-md bg-fuschia-700 px-6 py-3.5 text-base font-semibold text-white transition hover:scale-[1.02]">
                            Book a demo
                            <ArrowRight className="size-4" />
                        </a>
                        <a
                            href="#"
                            className="inline-flex items-center gap-2 rounded-md border border-white/20 px-6 py-3.5 text-base font-semibold text-white transition hover:bg-white/10">
                            Evaluate dotCMS
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
